import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import { dashboardAPI, financeAPI } from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { DollarSign, ShoppingCart, Coins, Users, TrendingUp, Package, Gift, Percent, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';
import MonthlyReportExport from './MonthlyReportExport';

// Formatação brasileira para moedas (inteiros) - Ex: 1.000.000
const formatCoins = (value) => {
  return Math.floor(value || 0).toLocaleString('pt-BR');
};

// Formatação brasileira para valores monetários - Ex: 1.000,00
const formatMoney = (value) => {
  return (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const DashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [balances, setBalances] = useState(null);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [days, setDays] = useState(1); // Padrão: Hoje
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  useEffect(() => {
    loadStats();
    loadBalances();
    loadFinanceSummary();
    loadPeakHours();
  }, [days]);

  const errMsg = (error, fallback) =>
    error?.response?.status === 503 ? 'Banco de dados demorou para responder. Tente novamente em instantes.'
      : error?.response?.data?.detail || fallback;

  const loadFinanceSummary = async () => {
    try {
      const response = await financeAPI.getSummary(days);
      console.log('✅ Finance summary carregado:', response.data);
      setFinanceSummary(response.data);
    } catch (error) {
      console.error('Erro ao carregar resumo financeiro:', error);
      toast.error(errMsg(error, 'Erro ao carregar resumo financeiro'));
    }
  };

  const loadPeakHours = async () => {
    try {
      const response = await dashboardAPI.getPeakHours(days);
      console.log('✅ Horários de pico carregados:', response.data);
      setPeakHours(response.data);
    } catch (error) {
      console.error('Erro ao carregar horários de pico:', error);
    }
  };

  const loadBalances = async () => {
    try {
      const response = await dashboardAPI.getBalances(days);
      console.log('✅ Saldos carregados:', response.data);
      setBalances(response.data);
    } catch (error) {
      console.error('❌ Erro ao carregar saldos:', error);
      toast.error(errMsg(error, 'Erro ao carregar saldos'));
    }
  };

  const loadStats = async () => {
    setLoading(true);
    setStatsError(null);
    console.log('📊 Carregando dashboard stats, days:', days);
    try {
      const response = await dashboardAPI.getStats(days);
      console.log('✅ Dashboard stats carregadas:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('❌ Erro ao carregar dashboard:', error);
      console.error('Detalhes:', error.response?.data);
      const msg = errMsg(error, 'Erro ao carregar estatísticas');
      setStatsError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!loading && !stats && statsError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center" data-testid="dashboard-error">
        <p className="text-sm text-red-600">{statsError}</p>
        <button
          type="button"
          onClick={loadStats}
          className="px-4 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600"
          data-testid="dashboard-retry-btn"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  const { period_stats, sales_chart, top_customers, top_packages, orders_by_status } = stats;

  return (
    <div className="space-y-6" data-testid="dashboard-stats">
      {/* Período - MOVIDO PARA O TOPO */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold">📊 Estatísticas por Período</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-full sm:w-[190px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Hoje</SelectItem>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="15">Últimos 15 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
              <SelectItem value="365">Último ano</SelectItem>
              <SelectItem value="9999">Todo o período</SelectItem>
            </SelectContent>
          </Select>
          <MonthlyReportExport />
        </div>
      </div>

      {/* Saldos e Métricas Financeiras - REFLETEM O PERÍODO */}
      {balances && (
        <>
          {/* Mobile: Uma única caixa COM SEÇÕES */}
          <Card className="md:hidden">
            <CardHeader>
              <CardTitle>Resumo Financeiro</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* SALDOS (Tempo Real) */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 mb-2">💰 SALDOS (Tempo Real)</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="text-sm">🟢 Saldo EFI</span>
                    <span className="font-bold text-green-600">R$ {formatMoney(balances.efi_bank_balance)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-purple-50 rounded" data-testid="stripe-balance-mobile">
                    <span className="text-sm">🟣 Saldo Stripe</span>
                    <div className="text-right">
                      <span className="font-bold text-purple-600">$ {formatMoney(balances.stripe_balance_available)}</span>
                      {(balances.stripe_balance_pending ?? 0) > 0 && (
                        <span className="text-xs text-orange-500 ml-1">(+$ {formatMoney(balances.stripe_balance_pending)})</span>
                      )}
                      <div>
                        <span className="font-bold text-indigo-600" data-testid="stripe-balance-eur-mobile">
                          € {formatMoney(balances.stripe_balance_available_eur ?? 0)}
                        </span>
                        {(balances.stripe_balance_pending_eur ?? 0) > 0 && (
                          <span className="text-xs text-orange-500 ml-1">(+€ {formatMoney(balances.stripe_balance_pending_eur)})</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-2 bg-blue-50 rounded" data-testid="kako-balance-mobile">
                    <p className="text-sm mb-1">🪙 Saldo KAKO</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Saldo real</span>
                      <span className="text-sm font-bold text-blue-600">{formatCoins(balances.kako_balance)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">🔒 Reservado</span>
                      <span className="text-sm font-bold text-orange-600">{formatCoins(balances.kako_reserved_coins ?? 0)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">🟢 Disponível</span>
                      <span className={`text-sm font-bold ${(balances.kako_available_coins ?? 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCoins(balances.kako_available_coins ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ESTATÍSTICAS */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 mb-2">📊 ESTATÍSTICAS</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="text-sm">Receita Total</span>
                    <span className="font-bold text-green-600">R$ {formatMoney(period_stats.total_revenue)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                    <span className="text-sm">Clientes Únicos</span>
                    <span className="font-bold text-blue-600">{period_stats.unique_customers}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                    <span className="text-sm">Ticket Médio</span>
                    <span className="font-bold text-purple-600">R$ {formatMoney(period_stats.avg_ticket)}</span>
                  </div>
                </div>
              </div>

              {/* LUCRO */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 mb-2">💵 LUCRO</h4>
                <div className="space-y-2">
              <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                <span className="text-sm">Lucro Bruto</span>
                <span className="font-bold text-yellow-600">
                  R$ {formatMoney(balances.gross_profit)}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                <span className="text-sm">Lucro Líquido</span>
                <span className="font-bold text-green-600">R$ {formatMoney(balances.net_profit)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                <span className="text-sm">Lucro Final</span>
                <span className="font-bold text-blue-600">R$ {formatMoney(balances.final_profit)}</span>
              </div>
                </div>
              </div>

              {/* CUSTOS (Período) */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 mb-2">📉 CUSTOS E DESPESAS</h4>
                <div className="space-y-2">
              <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                <div>
                  <div className="text-sm">Custo Moedas</div>
                  <div className="text-xs text-orange-500">{formatCoins(balances.coins_sold)} 🪙</div>
                </div>
                <span className="font-bold text-orange-600">R$ {formatMoney(balances.coins_sold_cost)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                <div>
                  <span className="text-sm">Taxas Gateway + IOF</span>
                  {(balances.stripe_orders_count > 0) && (
                    <div className="text-xs text-gray-500">
                      🟣 Stripe: R$ {formatMoney(balances.stripe_fees)} + IOF R$ {formatMoney(balances.stripe_iof)}
                    </div>
                  )}
                  {(balances.efi_orders_count > 0) && (
                    <div className="text-xs text-gray-500">
                      🟢 EFI: R$ {formatMoney(balances.efi_fees)}
                    </div>
                  )}
                  {(balances.mbway_orders_count > 0) && (
                    <div className="text-xs text-gray-500">
                      📱 MB WAY: €{formatMoney(balances.mbway_fees)}
                    </div>
                  )}
                </div>
                <span className="font-bold text-orange-600">R$ {formatMoney((balances.gateway_fees || 0) + (balances.iof_total || 0))}</span>
              </div>
              {financeSummary && (
                <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                  <span className="text-sm">Imposto ({balances.tax_percentage ?? financeSummary.tax_percentage ?? 0}%)</span>
                  <span className="font-bold text-red-600">R$ {formatMoney(balances.tax_amount ?? financeSummary?.tax_amount)}</span>
                </div>
              )}
                </div>
              </div>

              {/* RECOMPENSAS DADAS */}
              <div className="p-2 bg-purple-50 rounded">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <div className="text-sm font-semibold">Recompensas Dadas</div>
                    <div className="text-xs text-purple-500">{formatCoins(balances.total_rewards_coins)} 🪙</div>
                  </div>
                  <span className="font-bold text-purple-600">R$ {formatMoney(balances.total_rewards_cost)}</span>
                </div>
                <div className="space-y-1 pl-2 border-l-2 border-purple-200">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">💰 Ranking Diário:</span>
                    <span className="font-medium text-purple-600">
                      {formatCoins(balances.ranking_rewards_coins)} 🪙 - R$ {formatMoney(balances.ranking_rewards_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">⭐ Tarefas VIP:</span>
                    <span className="font-medium text-purple-600">
                      {formatCoins(balances.task_rewards_coins)} 🪙 - R$ {formatMoney(balances.task_rewards_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">🤝 Tarefas Afiliado:</span>
                    <span className="font-medium text-purple-600">
                      {formatCoins(balances.reseller_rewards_coins)} 🪙 - R$ {formatMoney(balances.reseller_rewards_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">🎁 Bônus Pacotes:</span>
                    <span className="font-medium text-purple-600">
                      {formatCoins(balances.bonus_coins_given)} 🪙 - R$ {formatMoney(balances.bonus_coins_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">🌟 Ranking Estrelas:</span>
                    <span className="font-medium text-purple-600">
                      {formatCoins(balances.star_ranking_coins)} 🪙 - R$ {formatMoney(balances.star_ranking_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">💼 Trabalhou Ganhou:</span>
                    <span className="font-medium text-purple-600">
                      {(balances.host_work_coins ?? 0).toLocaleString()} 🪙 - R$ {(balances.host_work_cost ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">📦 Baú Misterioso:</span>
                    <span className="font-medium text-purple-600">
                      {(balances.mystery_box_coins ?? 0).toLocaleString()} 🪙 - R$ {(balances.mystery_box_cost ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">🎰 Jackpot Baú:</span>
                    <span className="font-medium text-purple-600">
                      {(balances.jackpot_coins ?? 0).toLocaleString()} 🪙 - R$ {(balances.jackpot_cost ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs" data-testid="roulette-coins-summary">
                    <span className="text-gray-600">🎡 Roleta Azulzinhas:</span>
                    <span className="font-medium text-purple-600">
                      {(balances.roulette_coins ?? 0).toLocaleString()} 🪙 - R$ {(balances.roulette_cost ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* FUNDOS */}
              {financeSummary && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 mb-2">🐷 FUNDOS</h4>
                  <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                    <span className="text-sm">Reserva Total</span>
                    <span className="font-bold text-orange-600">R$ {(financeSummary.total_reserved ?? 0).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Desktop/Tablet: Cards agrupados por seções */}
          <div className="hidden md:block space-y-6">
            {/* 1. SALDOS */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">💰 SALDOS (Tempo Real)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 grid-cols-3">
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-gray-600 mb-1">🟢 Saldo EFI</p>
            <p className="text-2xl font-bold text-green-600">R$ {formatMoney(balances.efi_bank_balance)}</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg border border-purple-200" data-testid="stripe-balance-card">
            <p className="text-sm text-gray-600 mb-1">🟣 Saldo Stripe</p>
            <p className="text-2xl font-bold text-purple-600">$ {formatMoney(balances.stripe_balance_available)}</p>
            {(balances.stripe_balance_pending ?? 0) > 0 && (
              <p className="text-xs text-orange-500">+ $ {formatMoney(balances.stripe_balance_pending)} pendente</p>
            )}
            <p className="text-2xl font-bold text-indigo-600 mt-1" data-testid="stripe-balance-eur">
              € {formatMoney(balances.stripe_balance_available_eur ?? 0)}
            </p>
            {(balances.stripe_balance_pending_eur ?? 0) > 0 && (
              <p className="text-xs text-orange-500">+ € {formatMoney(balances.stripe_balance_pending_eur)} pendente</p>
            )}
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="kako-balance-card">
            <p className="text-sm text-gray-600 mb-2">🪙 Saldo KAKO</p>
            <div className="space-y-1.5">
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-gray-600">Saldo real</span>
                <span className="text-xl font-bold text-blue-600">{formatCoins(balances.kako_balance)}</span>
              </div>
              <div className="flex justify-between items-baseline" data-testid="kako-reserved-desktop">
                <span className="text-sm text-gray-600">🔒 Reservado</span>
                <span className="text-xl font-bold text-orange-600">{formatCoins(balances.kako_reserved_coins ?? 0)}</span>
              </div>
              <div className="flex justify-between items-baseline" data-testid="kako-available-desktop">
                <span className="text-sm text-gray-600">🟢 Disponível</span>
                <span className={`text-xl font-bold ${(balances.kako_available_coins ?? 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCoins(balances.kako_available_coins ?? 0)}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1.5 pt-1.5 border-t border-blue-200">Custo: R$ {formatMoney(balances.kako_balance_cost)}</p>
          </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. ESTATÍSTICAS */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">📊 ESTATÍSTICAS</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 grid-cols-3">
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Receita Total</p>
            <p className="text-2xl font-bold text-green-600">R$ {formatMoney(period_stats.total_revenue)}</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Clientes Únicos</p>
            <p className="text-2xl font-bold text-blue-600">{period_stats.unique_customers}</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Ticket Médio</p>
            <p className="text-2xl font-bold text-purple-600">R$ {formatMoney(period_stats.avg_ticket)}</p>
          </div>
                </div>
              </CardContent>
            </Card>

            {/* 3. LUCRO */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">💵 LUCRO</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 grid-cols-3">
          <div className="p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Lucro Bruto</p>
            <p className="text-xl font-bold text-yellow-600">
              R$ {formatMoney(balances.gross_profit)}
            </p>
            <p className="text-xs text-gray-500">Receita - Custo</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Lucro Líquido</p>
            <p className="text-xl font-bold text-green-600">R$ {formatMoney(balances.net_profit)}</p>
            <p className="text-xs text-gray-500">Bruto - Tax - Imp.</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Lucro Final</p>
            <p className="text-xl font-bold text-blue-600">R$ {formatMoney(balances.final_profit)}</p>
            <p className="text-xs text-gray-500">Líq. - Fundos - Recomp.</p>
          </div>
                </div>
              </CardContent>
            </Card>

            {/* 4. CUSTOS */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">📉 CUSTOS E DESPESAS</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <div className="p-3 bg-orange-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Custo Moedas</p>
            <p className="text-xs text-orange-500 mb-1">{formatCoins(balances.coins_sold)} 🪙</p>
            <p className="text-2xl font-bold text-orange-600">R$ {formatMoney(balances.coins_sold_cost)}</p>
          </div>
          <div className="p-3 bg-orange-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Taxas Gateway + IOF</p>
            <p className="text-2xl font-bold text-orange-600 mb-2">R$ {formatMoney((balances.gateway_fees || 0) + (balances.iof_total || 0))}</p>
            <div className="space-y-1 pt-2 border-t border-orange-200">
              {(balances.efi_orders_count > 0) && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">🟢 EFI ({balances.efi_orders_count})</span>
                  <span className="font-medium text-orange-600">R$ {formatMoney(balances.efi_fees)}</span>
                </div>
              )}
              {(balances.stripe_orders_count > 0) && (
                <>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">🟣 Stripe ({balances.stripe_orders_count})</span>
                    <span className="font-medium text-orange-600">R$ {formatMoney(balances.stripe_fees)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600 pl-4">↳ IOF</span>
                    <span className="font-medium text-red-500">R$ {formatMoney(balances.stripe_iof)}</span>
                  </div>
                </>
              )}
              {(balances.mercadopago_orders_count > 0) && (
                <div className="flex justify-between items-center text-xs" data-testid="dash-fee-mercadopago">
                  <span className="text-gray-600">🔵 Mercado Pago ({balances.mercadopago_orders_count})</span>
                  <span className="font-medium text-orange-600">R$ {formatMoney(balances.mercadopago_fees)}</span>
                </div>
              )}
              {(balances.mbway_orders_count > 0) && (
                <>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">📱 MB WAY ({balances.mbway_orders_count}) · taxa do cliente</span>
                    <span className="font-medium text-gray-500">€{formatMoney(balances.mbway_fees)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600 pl-4">↳ Fixo (do cliente)</span>
                    <span className="font-medium text-gray-500">€{formatMoney(balances.mbway_fees_fixed)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          {financeSummary && (
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Imposto ({financeSummary.tax_percentage ?? 0}%)</p>
              <p className="text-2xl font-bold text-red-600">R$ {formatMoney(financeSummary.tax_amount)}</p>
            </div>
          )}
          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Recompensas Dadas</p>
            <p className="text-xs text-purple-500 mb-1">{formatCoins(balances.total_rewards_coins)} 🪙</p>
            <p className="text-2xl font-bold text-purple-600 mb-2">R$ {formatMoney(balances.total_rewards_cost)}</p>
            <div className="space-y-1 pt-2 border-t border-purple-200">
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">💰 Ranking Diário:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.ranking_rewards_coins)} 🪙 - R$ {formatMoney(balances.ranking_rewards_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">⭐ Tarefas VIP:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.task_rewards_coins)} 🪙 - R$ {formatMoney(balances.task_rewards_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">🤝 Tarefas Afiliado:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.reseller_rewards_coins)} 🪙 - R$ {formatMoney(balances.reseller_rewards_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">🎁 Bônus Pacotes:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.bonus_coins_given)} 🪙 - R$ {formatMoney(balances.bonus_coins_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">🌟 Ranking Estrelas:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.star_ranking_coins)} 🪙 - R$ {formatMoney(balances.star_ranking_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">💼 Trabalhou Ganhou:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.host_work_coins)} 🪙 - R$ {formatMoney(balances.host_work_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">📦 Baú Misterioso:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.mystery_box_coins)} 🪙 - R$ {formatMoney(balances.mystery_box_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">🎰 Jackpot Baú:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.jackpot_coins)} 🪙 - R$ {formatMoney(balances.jackpot_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs" data-testid="roulette-coins-detail">
                <span className="text-gray-600">🎡 Roleta Azulzinhas:</span>
                <span className="font-medium text-purple-600">
                  {formatCoins(balances.roulette_coins)} 🪙 - R$ {formatMoney(balances.roulette_cost)}
                </span>
              </div>
            </div>
          </div>
                </div>
              </CardContent>
            </Card>

            {/* 5. FUNDOS */}
            {financeSummary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">🐷 FUNDOS</CardTitle>
              </CardHeader>
              <CardContent>
          <div className="p-3 bg-orange-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Reserva Total</p>
            <p className="text-2xl font-bold text-orange-600">R$ {(financeSummary.total_reserved ?? 0).toFixed(2)}</p>
          </div>
              </CardContent>
            </Card>
            )}
          </div>
        </>
      )}

      {/* Status dos Pedidos */}
      <Card data-testid="orders-status-card">
        <CardHeader>
          <CardTitle>Status dos Pedidos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {Object.entries(orders_by_status).map(([status, count]) => (
              <Badge key={status} variant="secondary" className="px-4 py-2">
                {status}: {count}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Gráfico de Vendas */}
      <Card data-testid="sales-chart-card">
        <CardHeader>
          <CardTitle>Vendas por Dia - Análise Completa</CardTitle>
          <CardDescription>
            {days === 1 ? 'Vendas de hoje' : days === 9999 ? 'Todo o período' : `Últimos ${days} dias`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {sales_chart.map((day) => (
              <div key={day.date} className="border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{day.date.split('-').reverse().join('/')}</span>
                  <Badge variant="secondary">{day.orders} pedidos</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-8 gap-2 text-xs">
                  <div>
                    <p className="text-gray-500">Receita</p>
                    <p className="font-bold text-green-600">R$ {day.revenue.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Moedas Vendidas</p>
                    <p className="font-bold text-purple-600">{day.coins?.toLocaleString() || 0} 🪙</p>
                    <p className="font-bold text-orange-600">R$ {day.coins_cost?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Moedas Dadas</p>
                    <p className="font-bold text-purple-600">{formatCoins(day.rewards_coins)} 🪙</p>
                    <p className="font-bold text-orange-600">R$ {formatMoney(day.rewards_cost)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Taxas Gateway</p>
                    <p className="font-bold text-red-600">R$ {formatMoney(day.gateway_fees)}</p>
                    {(day.iof > 0) && (
                      <p className="text-xs text-red-500">+ IOF: R$ {formatMoney(day.iof)}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-500">Imposto</p>
                    <p className="font-bold text-red-600">R$ {formatMoney(day.tax)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Reserva</p>
                    <p className="font-bold text-orange-600">R$ {formatMoney(day.reserve)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Lucro Bruto</p>
                    <p className="font-bold text-blue-600">R$ {formatMoney(day.gross_profit)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Lucro Final</p>
                    <p className="font-bold text-blue-600">R$ {formatMoney(day.final_profit || day.net_profit)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Top Clientes */}
        <Card data-testid="top-customers-card">
          <CardHeader>
            <CardTitle>Top Clientes</CardTitle>
            <CardDescription>Maiores compradores do período</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {top_customers.map((customer, idx) => (
                <div key={customer.kako_id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium">
                      {idx === 0 && '🥇 '}
                      {idx === 1 && '🥈 '}
                      {idx === 2 && '🥉 '}
                      {customer.kako_nickname}
                    </p>
                    <p className="text-xs text-gray-600">{customer.order_count} pedidos</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">R$ {customer.total_amount.toFixed(2)}</p>
                    <p className="text-xs text-gray-600">{customer.total_coins} moedas</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Pacotes */}
        <Card data-testid="top-packages-card">
          <CardHeader>
            <CardTitle>Pacotes Mais Vendidos</CardTitle>
            <CardDescription>Mais populares do período</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {top_packages.map((pkg) => (
                <div key={pkg.package_id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-purple-600" />
                    <p className="font-medium">{pkg.package_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-purple-600">{pkg.count} vendas</p>
                    <p className="text-xs text-gray-600">R$ {pkg.revenue.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Gráfico de Horários de Pico */}
      {peakHours && (
        <Card data-testid="peak-hours-card">
          <CardHeader>
            <CardTitle>🔥 Horários de Pico</CardTitle>
            <CardDescription>
              Distribuição de pedidos por hora do dia - {days === 1 ? 'Hoje' : days === 9999 ? 'Todo o período' : `Últimos ${days} dias`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {/* Info de pico */}
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                <p className="text-sm font-semibold text-orange-900">
                  🔥 Horário de maior fluxo: <span className="text-lg">{peakHours.peak_hour}</span>
                </p>
                <p className="text-xs text-gray-600">
                  Total de {peakHours.total_orders} pedidos analisados
                </p>
              </div>
              
              {/* Gráfico de barras */}
              <div className="space-y-1">
                {peakHours.hours.map((hour) => {
                  const maxCount = Math.max(...peakHours.hours.map(h => h.count));
                  const percentage = maxCount > 0 ? (hour.count / maxCount) * 100 : 0;
                  
                  return (
                    <div key={hour.hour} className="flex items-center gap-2">
                      <span className="text-xs font-mono w-12 text-gray-600">{hour.hour}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            hour.is_peak 
                              ? 'bg-gradient-to-r from-orange-500 to-red-500' 
                              : 'bg-gradient-to-r from-blue-400 to-blue-600'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                        {/* Número em preto negrito com fundo claro - SEMPRE visível */}
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-900 bg-white/90 px-2 py-0.5 rounded shadow-sm">
                          {hour.count}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardStats;